/**
 * Renderiza o mapa de assentos como uma imagem PNG (canvas) para enviar ao cliente,
 * destacando livres / ocupadas / selecionadas. Retorna um File pronto para upload+envio.
 *
 * Reaproveita os dados já presentes em `assentos` (grid com {x,y,numero,ocupado},
 * lista de disponíveis e seleção por passageiro).
 */

function asInt(v) {
  const n = typeof v === "number" ? v : parseInt(String(v), 10);
  return Number.isFinite(n) ? n : null;
}

function asCoord(v) {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function uniqSorted(nums) {
  return Array.from(new Set(nums)).sort((a, b) => a - b);
}

/** Mesma heurística do BPeSeatMap: gap em Y ≥ 2 ou 2+2 / 3+3 → corredor. */
function pickAisleIndex(uniqueYs) {
  if (!uniqueYs || uniqueYs.length <= 1) return 0;
  let bestGapIdx = null;
  let bestGap = 1;
  for (let i = 0; i < uniqueYs.length - 1; i += 1) {
    const gap = uniqueYs[i + 1] - uniqueYs[i];
    if (gap > bestGap) {
      bestGap = gap;
      bestGapIdx = i;
    }
  }
  if (bestGapIdx != null && bestGap >= 2) return bestGapIdx;
  if (uniqueYs.length === 4) return 1;
  if (uniqueYs.length === 6) return 2;
  return Math.max(0, Math.floor((uniqueYs.length - 1) / 2));
}

function rowPixelY(row, aisleAfter, seat = SEAT, gap = GAP, aisleH = AISLE_H) {
  let y = row * (seat + gap);
  if (row > aisleAfter) y += aisleH;
  return y;
}

function deckPixelHeight(rows, aisleAfter, seat = SEAT, gap = GAP, aisleH = AISLE_H) {
  if (rows <= 0) return 0;
  return rows * seat + Math.max(0, rows - 1) * gap + aisleH;
}

function roundRect(ctx, x, y, w, h, r) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

const COLORS = {
  bg: "#ffffff",
  title: "#1a1a1a",
  muted: "#666666",
  free: "#2e7d32",
  freeBg: "#ffffff",
  occupied: "#bdbdbd",
  occupiedBg: "#eeeeee",
  selected: "#1565c0",
  text: "#1a1a1a",
};

const SEAT = 46;
const GAP = 8;
const PAD = 22;
const CAP_W = 28;
const CAP_GAP = 10;
const DECK_GAP = 22;
const DECK_LABEL_H = 20;
const AISLE_H = 14;

function drawSteeringWheel(ctx, cx, cy, r) {
  ctx.save();
  ctx.strokeStyle = COLORS.muted;
  ctx.fillStyle = COLORS.muted;
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.28, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  for (const a of [-Math.PI / 2, (Math.PI * 5) / 6, Math.PI / 6]) {
    ctx.moveTo(cx + Math.cos(a) * r * 0.28, cy + Math.sin(a) * r * 0.28);
    ctx.lineTo(cx + Math.cos(a) * r * 0.95, cy + Math.sin(a) * r * 0.95);
  }
  ctx.stroke();
  ctx.restore();
}

function deckLabel(index, total) {
  if (total <= 1) return "";
  if (index === 0) return "Piso inferior";
  if (index === 1) return "Piso superior";
  return `Piso ${index + 1}`;
}

/**
 * @param {Array} grid assentos.grid (cada item: { x, y, numero, ocupado, piso })
 * @param {{ disponiveis?: Array, selected?: object, title?: string }} opts
 * @returns {Promise<File>}
 */
export async function renderSeatMapImage(grid, { disponiveis = [], selected = {}, title = "" } = {}) {
  const cells = (grid || [])
    .map((s) => {
      const numero = asInt(s.numero);
      const rawLabel =
        numero == null && s.numero != null && String(s.numero).trim()
          ? String(s.numero).trim()
          : s.feature != null && String(s.feature).trim()
            ? String(s.feature).trim()
            : null;
      return {
        x: asCoord(s.x),
        y: asCoord(s.y),
        numero,
        ocupado: s.ocupado,
        piso: s.piso == null || s.piso === "" ? null : String(s.piso),
        feature: rawLabel,
        isSeat: numero != null,
      };
    })
    .filter((s) => s.x != null && s.y != null);

  const seatCells = cells.filter((s) => s.isSeat);
  const availSet = new Set((disponiveis || []).map((v) => String(asInt(v))).filter((v) => v !== "null"));
  const selSet = new Set(Object.values(selected || {}).map((v) => String(asInt(v))).filter((v) => v !== "null"));

  const isFree = (n, ocupado) => {
    if (availSet.size > 0) return availSet.has(String(n));
    return ocupado === 0;
  };

  const stateOf = (n, ocupado) =>
    selSet.has(String(n)) ? "selected" : isFree(n, ocupado) ? "free" : "occupied";

  const hasCoord = seatCells.length > 0 && cells.length >= Math.min(8, Math.max(seatCells.length, 1));

  const decks = [];
  if (hasCoord) {
    const groups = new Map();
    for (const s of cells) {
      const key = s.piso == null ? "__single__" : s.piso;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(s);
    }
    const minNum = (arr) =>
      arr.reduce((m, s) => (s.numero != null && s.numero < m ? s.numero : m), Infinity);
    const keys = [...groups.keys()].sort((a, b) => minNum(groups.get(a)) - minNum(groups.get(b)));
    for (const key of keys) {
      const arr = groups.get(key);
      const layoutCells = arr.filter((s) => s.isSeat || s.feature);
      if (!layoutCells.length) continue;
      const xs = uniqSorted(layoutCells.map((s) => s.x));
      const ys = uniqSorted(arr.map((s) => s.y));
      const aisleAfter = pickAisleIndex(ys);
      const placed = layoutCells.map((s) => ({
        col: xs.indexOf(s.x),
        row: ys.indexOf(s.y),
        numero: s.numero,
        feature: s.feature,
        isSeat: s.isSeat,
        state: s.isSeat ? stateOf(s.numero, s.ocupado) : "spacer",
      }));
      decks.push({ cols: xs.length, rows: ys.length, aisleAfter, placed });
    }
  } else {
    const list = uniqSorted((disponiveis || []).map((v) => asInt(v)).filter((n) => n != null));
    const cols = Math.min(8, Math.max(1, list.length));
    const rows = Math.ceil(list.length / cols) || 1;
    const placed = list.map((n, i) => ({
      col: i % cols,
      row: Math.floor(i / cols),
      numero: n,
      isSeat: true,
      state: selSet.has(String(n)) ? "selected" : "free",
    }));
    decks.push({ cols, rows, aisleAfter: pickAisleIndex([...Array(rows).keys()]), placed });
  }

  const multiDeck = decks.length > 1;
  const maxCols = Math.max(...decks.map((d) => d.cols));
  const deckLabelH = multiDeck ? DECK_LABEL_H : 0;

  const headerH = title ? 60 : 34;
  const legendH = 26;

  const legendItems = [
    { label: "Livre", border: COLORS.free, bg: COLORS.freeBg, text: COLORS.free },
    { label: "Ocupada", border: COLORS.occupied, bg: COLORS.occupiedBg, text: COLORS.muted },
    { label: "Selecionada", border: COLORS.selected, bg: COLORS.selected, text: "#ffffff" },
  ];

  const gridW = CAP_W + CAP_GAP + maxCols * SEAT + (maxCols - 1) * GAP;
  let decksH = 0;
  decks.forEach((d, i) => {
    decksH += deckLabelH + deckPixelHeight(d.rows, d.aisleAfter);
    if (i < decks.length - 1) decksH += DECK_GAP;
  });

  const measureCtx = document.createElement("canvas").getContext("2d");
  const measure = (text, font) => {
    measureCtx.font = font;
    return measureCtx.measureText(text).width;
  };

  let contentW = 0;
  if (title) {
    contentW = Math.max(
      contentW,
      measure(String(title).slice(0, 60), "bold 16px Arial, sans-serif"),
      measure("Escolha sua poltrona (fundo → frente)", "12px Arial, sans-serif")
    );
  } else {
    contentW = Math.max(contentW, measure("Mapa de assentos (fundo → frente)", "12px Arial, sans-serif"));
  }
  let legendW = 0;
  for (const it of legendItems) legendW += 22 + measure(it.label, "11px Arial, sans-serif") + 18;
  contentW = Math.max(contentW, Math.max(0, legendW - 18));

  const width = Math.max(PAD * 2 + gridW, PAD * 2 + Math.ceil(contentW));

  const freeNums = decks
    .flatMap((d) => d.placed)
    .filter((p) => p.state === "free" && p.numero != null)
    .map((p) => String(p.numero).padStart(2, "0"))
    .sort((a, b) => Number(a) - Number(b));
  const footerLines = [];
  if (freeNums.length) {
    const maxLineW = width - PAD * 2;
    const prefix = "Livres: ";
    let line = prefix;
    freeNums.forEach((num, i) => {
      const piece = num + (i < freeNums.length - 1 ? ", " : "");
      if (line !== prefix && measure(line + piece, "11px Arial, sans-serif") > maxLineW) {
        footerLines.push(line);
        line = piece;
      } else {
        line += piece;
      }
    });
    if (line.trim()) footerLines.push(line);
  }
  const footerH = footerLines.length * 18 + 16;

  const height = headerH + legendH + decksH + footerH + PAD;

  const canvas = document.createElement("canvas");
  const scale = window.devicePixelRatio && window.devicePixelRatio > 1 ? 2 : 1;
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext("2d");
  ctx.scale(scale, scale);

  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, width, height);

  ctx.textBaseline = "top";
  if (title) {
    ctx.fillStyle = COLORS.title;
    ctx.font = "bold 16px Arial, sans-serif";
    ctx.fillText(String(title).slice(0, 60), PAD, 14);
    ctx.fillStyle = COLORS.muted;
    ctx.font = "12px Arial, sans-serif";
    ctx.fillText("Escolha sua poltrona (fundo → frente)", PAD, 36);
  } else {
    ctx.fillStyle = COLORS.muted;
    ctx.font = "12px Arial, sans-serif";
    ctx.fillText("Mapa de assentos (fundo → frente)", PAD, 12);
  }

  const legendY = headerH;
  let lx = PAD;
  ctx.font = "11px Arial, sans-serif";
  for (const it of legendItems) {
    roundRect(ctx, lx, legendY, 16, 16, 4);
    ctx.fillStyle = it.bg;
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = it.border;
    ctx.stroke();
    ctx.fillStyle = COLORS.muted;
    ctx.fillText(it.label, lx + 22, legendY + 2);
    lx += 22 + ctx.measureText(it.label).width + 18;
  }

  const seatsAreaW = maxCols * SEAT + (maxCols - 1) * GAP;
  const seatsRight = PAD + seatsAreaW;
  const capX = seatsRight + CAP_GAP;
  let cursorY = headerH + legendH;
  decks.forEach((deck, di) => {
    if (multiDeck) {
      ctx.fillStyle = COLORS.muted;
      ctx.font = "bold 11px Arial, sans-serif";
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillText(deckLabel(di, decks.length).toUpperCase(), PAD, cursorY + 4);
      cursorY += deckLabelH;
    }
    const deckTop = cursorY;
    const deckH = deckPixelHeight(deck.rows, deck.aisleAfter);

    if (di === 0) {
      roundRect(ctx, capX, deckTop, CAP_W, deckH, 12);
      ctx.fillStyle = COLORS.occupiedBg;
      ctx.fill();
      ctx.lineWidth = 1;
      ctx.strokeStyle = COLORS.occupied;
      ctx.stroke();
      drawSteeringWheel(ctx, capX + CAP_W / 2, deckTop + 16, 9);
      ctx.save();
      ctx.translate(capX + CAP_W / 2, deckTop + deckH - 8);
      ctx.rotate(-Math.PI / 2);
      ctx.fillStyle = COLORS.muted;
      ctx.font = "bold 9px Arial, sans-serif";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText("FRENTE", 0, 0);
      ctx.restore();
    }

    if (deck.rows > 1) {
      const aisleY = deckTop + rowPixelY(deck.aisleAfter + 1, deck.aisleAfter) - AISLE_H;
      ctx.save();
      ctx.strokeStyle = "#cccccc";
      ctx.setLineDash([4, 3]);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(PAD, aisleY + AISLE_H / 2);
      ctx.lineTo(seatsRight, aisleY + AISLE_H / 2);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    }

    for (const p of deck.placed) {
      if (!p.isSeat && !p.feature) continue;
      const x = seatsRight - SEAT - p.col * (SEAT + GAP);
      const y = deckTop + rowPixelY(p.row, deck.aisleAfter);

      if (!p.isSeat && p.feature) {
        roundRect(ctx, x, y, SEAT, SEAT, 8);
        ctx.fillStyle = COLORS.occupiedBg;
        ctx.fill();
        ctx.lineWidth = 1;
        ctx.strokeStyle = COLORS.occupied;
        ctx.stroke();
        ctx.fillStyle = COLORS.muted;
        ctx.font = "bold 10px Arial, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(String(p.feature).slice(0, 4), x + SEAT / 2, y + SEAT / 2 + 1);
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        continue;
      }

      let bg;
      let border;
      let txt;
      if (p.state === "selected") {
        bg = COLORS.selected;
        border = COLORS.selected;
        txt = "#ffffff";
      } else if (p.state === "occupied") {
        bg = COLORS.occupiedBg;
        border = COLORS.occupied;
        txt = COLORS.muted;
      } else {
        bg = COLORS.freeBg;
        border = COLORS.free;
        txt = COLORS.text;
      }
      roundRect(ctx, x, y, SEAT, SEAT, 8);
      ctx.fillStyle = bg;
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = border;
      ctx.stroke();
      ctx.fillStyle = txt;
      ctx.font = "bold 15px Arial, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(String(p.numero).padStart(2, "0"), x + SEAT / 2, y + SEAT / 2 + 1);
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
    }

    cursorY = deckTop + deckH + DECK_GAP;
  });

  let fy = headerH + legendH + decksH + 12;
  ctx.fillStyle = COLORS.muted;
  ctx.font = "11px Arial, sans-serif";
  for (const line of footerLines) {
    ctx.fillText(line, PAD, fy, width - PAD * 2);
    fy += 18;
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Não foi possível gerar a imagem do mapa de assentos."));
        return;
      }
      resolve(new File([blob], "mapa-assentos.png", { type: "image/png" }));
    }, "image/png");
  });
}
